import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/jsx-no-bind */
import React, { useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Button } from 'reactstrap';
import withJobActions from './withJobActions';
import TieredSelect from '../../../common/lookups/tieredSelect';
import JobPieceOther from '../../../admin/jobs/jobPieceOther';
import entityComposition from '../../../common/classes/entityComposition';

/**
 * Logic to transform incoming piece options to a structure that
 * TieredSelect can understand.  We only support a single tier.
 * @param {Array} pieceOptions - The options available to the user
 */
const transformPieceOptionsToTieredSelect = pieceOptions => {
  let sections = [];
  // Generate section names
  pieceOptions.forEach(piece => !sections.some(section => section.sectionName === piece.sectionName) && sections.push(piece));
  // Get all tier 1 sections
  sections = sections.map(section => _objectSpread(_objectSpread({}, section), {}, {
    name: section.sectionName
  }));
  // Add all children to tier 1
  sections = sections.map(section => {
    const node = _objectSpread({}, section);
    node.entity = 'JobPiece';
    const children = pieceOptions.filter(piece => piece.sectionName === node.sectionName);
    // Add entity and nodes to each child.
    node.nodes = children.map(piece => _objectSpread(_objectSpread({}, piece), {}, {
      nodes: [],
      entity: 'JobPiece'
    }));
    return node;
  });
  return sections;
};

/**
 * Adds entities so TieredSelect can understand which options are
 * selected.
 * @param {Array} pieces - Set of already selected pieces
 */
const addEntity = pieces => {
  let result = [];
  if (pieces) {
    result = pieces.map(apiece => {
      const piece = apiece;
      piece.entity = 'JobPiece';
      if (piece.nodes) {
        piece.nodes = addEntity(piece.nodes);
      }
      return piece;
    });
  }
  return result;
};
const JobPieceSubsection = props => {
  const active = props.active,
    onJobAdd = props.onJobAdd,
    onJobChange = props.onJobChange,
    onJobRemove = props.onJobRemove,
    serverIndex = props.jobPieceActions.serverIndex,
    jobPiece = props.jobPiece;
  const pieces = active.pieces,
    id = active.id,
    piecesOther = active.piecesOther;
  const list = jobPiece.list,
    indexQuery = jobPiece.indexQuery;
  useEffect(() => {
    serverIndex(indexQuery);
  }, [indexQuery, serverIndex]);
  const onChangeOther = useCallback((index, field, value) => onJobChange("piecesOther/".concat(index, "/").concat(field), value), [onJobChange]);
  const onRemoveOther = useCallback(index => onJobRemove('piecesOther', index), [onJobRemove]);
  const addOther = useCallback(() => {
    onJobAdd('piecesOther', {
      name: '',
      jobID: id
    });
  }, [id, onJobAdd]);

  /**
   * Remove or add all the selectedPiece's children to the list depending
   * on whether or not any were already selected.
   * @param {Object} selectedPiece - The piece that was newly selected
   */
  const handleParentClick = useCallback(selectedPiece => {
    // Check if any of the children have been previously selected
    const shouldClearSelected = pieces.some(piece => selectedPiece.nodes.findIndex(node => node.id === piece.id) !== -1);
    // Remove any selected children
    let mutatedList = pieces.filter(piece => selectedPiece.nodes.findIndex(node => node.id === piece.id) === -1);
    if (!shouldClearSelected) {
      // Add all children back in
      mutatedList.push(...selectedPiece.nodes);
    }
    // Ensure we're passing clean data
    mutatedList = mutatedList.map(piece => ({
      id: piece.id,
      name: piece.name,
      sectionName: piece.sectionName
    }));
    // Pass the whole list and let React and the server handle the update
    onJobChange('pieces', mutatedList);
  }, [onJobChange, pieces]);

  /**
   * Try to find the selected piece in our data store.  If the
   * piece is present, remove it, otherwise append.
   * @param {Object} selectedPiece - The piece that was newly selected
   */
  const handleChildClick = useCallback(selectedPiece => {
    // Attempt to find the index of the clicked
    const index = pieces.findIndex(option => option.id === selectedPiece.id);
    // Piece already exists, must be remove
    if (index !== -1) {
      onJobRemove('pieces', index);
      return;
    }
    onJobAdd('pieces', {
      id: selectedPiece.id,
      name: selectedPiece.name,
      sectionName: selectedPiece.sectionName
    });
  }, [onJobAdd, onJobRemove, pieces]);

  /**
   * handleChange does two things depending on the event
   *   1. Handles a parent being clicked
   *   2. Handles a child click
   * In the case of #2 a transform needs to be applied *from*
   * TieredSelect data item to Redux compatible data.
   *
   * In the case of #1 we want to select everything if no child
   * has been selected.  If a child is selected, deselect every child.
   * @param {Array} selectedList - List of all selected pieces
   * @param {Object} clicked - The piece that was just clicked
   */
  const handleChange = useCallback((selectedList, clicked) => {
    // Check for parent node click
    if (clicked.nodes.length > 0) {
      handleParentClick(clicked);
    } else {
      handleChildClick(clicked);
    }
  }, [handleChildClick, handleParentClick]);
  const data = useMemo(() => transformPieceOptionsToTieredSelect(list), [list]);
  const value = useMemo(() => addEntity(pieces), [pieces]);
  return /*#__PURE__*/React.createElement("form", null, /*#__PURE__*/React.createElement("h2", null, "Pieces"), /*#__PURE__*/React.createElement("hr", null), /*#__PURE__*/React.createElement("section", null, /*#__PURE__*/React.createElement(TieredSelect, {
    onChange: handleChange,
    data: data,
    value: value,
    backLabel: "Job Pieces"
  }), piecesOther ? piecesOther.map((otherPiece, index) => /*#__PURE__*/React.createElement(JobPieceOther, {
    index: index,
    onChange: onChangeOther,
    onRemove: onRemoveOther,
    entity: "PieceOther",
    field: "name",
    value: otherPiece.name,
    key: otherPiece.id,
    hideLabel: true,
    placeholder: "Piece"
  })) : [], /*#__PURE__*/React.createElement("hr", null), /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    sm: {
      size: 3,
      offset: 9
    }
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: addOther,
    className: "t-jp-add-other"
  }, "Add Other")))));
};
JobPieceSubsection.propTypes = {
  active: PropTypes.shape({
    id: PropTypes.string,
    pieces: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string
    })),
    piecesOther: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string
    }))
  }).isRequired,
  jobPieceActions: PropTypes.shape({
    serverIndex: PropTypes.func
  }).isRequired,
  jobPiece: PropTypes.shape({
    list: PropTypes.arrayOf(PropTypes.shape({})),
    indexQuery: PropTypes.shape({})
  }).isRequired,
  onJobAdd: PropTypes.func.isRequired,
  onJobChange: PropTypes.func.isRequired,
  onJobRemove: PropTypes.func.isRequired
};
export { JobPieceSubsection as UncomposedJobPiecesSubsection };
const Pieces = entityComposition(JobPieceSubsection, ['jobPiece'], {});
export default withJobActions(Pieces);